import React from 'react';
import { useRecoilValue } from 'recoil';
import { CardBody } from '../../components/CardBody';
import { CardFooter } from '../../components/CardFooter';
import { isMobileState } from '../../recoil/selectors';
import { BoldnessTankLanderMobile } from './BoldnessTankLanderMobile';
import { HeroCallout } from '../HeroCallout';
import { WistiaPanel } from '../../components/WistiaPanel';
import { NewsIcons } from '../../product_detail/components/NewsIcons';
import { ShopAllColors } from '../components/ShopAllColors';
import { ProductFeatures } from '../components/ProductFeatures';
import { ProductComparison } from '../components/ProductComparison';
import { LanderTopHero } from '../components/LanderTopHero';
import { allProductsState } from '../../recoil/atoms';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { useLanderControls } from '../LanderContext';
import { ProductReviewQuote } from '../components/ProductReviewQuote';
import { useProductAssets } from '../../hooks/useProductAssets';

export const BoldnessTankLander: React.FC = () => {
  const allProducts = useRecoilValue(allProductsState);
  const isMobile = useRecoilValue(isMobileState);
  const product = allProducts[ProductHandle.BoldnessTank];
  const productAssets = useProductAssets(product);
  const wistiaVideoId = productAssets.videos[0]?.wistiaId;
  const padding = { padding: '56.25% 0 0 0' };
  const { scrollToProductSection } = useLanderControls();

  if (isMobile) {
    return <BoldnessTankLanderMobile />;
  }

  return (
    <>
      <CardBody>
        <LanderTopHero
          maxHeroContainerHeight={'1066px'}
          heroImage={'desktop-boldness-tank-lander-top-hero-1.jpg'}
          title={'Boldness Tank'}
          subTitle={'The world’s most powerful sculpting tank.'}
        >
          <HeroCallout
            orderIndex={0}
            label={<>Underwire-free lift</>}
            color="black"
            labelColor="black"
            titleStyles={{ lineHeight: '22px', textAlign: 'right' }}
            inlineStyles={{
              desktop: {
                right: `84%`,
                top: `45%`,
                flexDirection: 'row',
              },
            }}
          />
          <HeroCallout
            orderIndex={1}
            color="black"
            labelColor="black"
            titleStyles={{ lineHeight: '22px' }}
            label={
              <>
                Serious waist
                <br /> contouring
              </>
            }
            inlineStyles={{
              desktop: {
                left: `28.5%`,
                top: `55%`,
                flexDirection: 'row-reverse',
              },
            }}
          />
          <HeroCallout
            orderIndex={2}
            color="black"
            labelColor="black"
            label={
              <>
                Bonded
                <br /> compression
              </>
            }
            titleStyles={{ lineHeight: '22px', textAlign: 'right' }}
            inlineStyles={{
              desktop: {
                left: `10.5%`,
                top: `59%`,
                flexDirection: 'row',
              },
            }}
          />
        </LanderTopHero>
        <NewsIcons />
        <ProductFeatures
          descriptionMaxWidth="400px"
          features={[
            {
              imgName: 'feature-bt-desktop-1.jpg',
              title: 'Extreme sculpting',
              description: 'Strong, bonded panels and 360° boning provide maximum body sculpting.',
            },
            {
              imgName: 'feature-bt-desktop-2.jpg',
              title: 'Ditch your bra',
              description: 'Ultra supportive, molded bust support provides lift without underwire.',
            },
            {
              imgName: 'feature-bt-desktop-3.jpg',
              title: 'Stylish and supportive',
              description: 'Smart details and flattering features make a confidence-boosting fashion statement.',
            },
          ]}
        />
        <ProductComparison
          onCtaClick={scrollToProductSection}
          competitorImage="boldness-bodysuit-competitor.jpg"
          honeyloveImage="boldness-bodysuit-honeylove.jpg"
          honeyloveText="Boldness Tank"
          competitorText="Competitor"
          title={
            <>
              Innovation you
              <br /> can see.
            </>
          }
        />
        <ProductReviewQuote
          backgroundColor="#EFEFEF"
          leftPanelMaxWidth={'50%'}
          rightPanelStyles={{ flex: 1 }}
          rightPanelMaxWidth={'720px'}
          quotes={[
            {
              quoteText: `“This is a magic tank top. It instantly turns me into a curvy goddess in a way I did not think was possible. It’s like instant confidence.”`,
              author: '- Kara B.',
            },
            {
              quoteText: `“This is a seriously intense tank, but it’s surprisingly comfortable when it’s on. Sometimes you want to turn the volume up to 100, and this is the top to do it!”`,
              author: '- Janise',
            },
            {
              quoteText: `“I gave this a try because Honeylove has a good return policy not knowing what to expect - but you couldn’t wrestle this thing out of my hands if you tried. I have NEVER felt more snatched in my life and it is all because of this phenomenal tank.”`,
              author: '- Bailey W.',
            },
          ]}
          imgName="bt-quote-1.jpg"
        />
        {wistiaVideoId && (
          <div style={{ padding: '50px 60px', paddingTop: 40, boxSizing: 'border-box' }}>
            <WistiaPanel videoId={wistiaVideoId} responsivePadding={padding} />
          </div>
        )}
        <ShopAllColors product={allProducts[ProductHandle.BoldnessTank]} />
      </CardBody>
      <CardFooter />
    </>
  );
};

export default BoldnessTankLander;
